import { styled } from '@mui/material/styles';
import { Drawer, DrawerProps, Toolbar } from '@mui/material';

import { BACKGROUND, PEARL, PRIMARY, STROKE } from '../../shared/Helpers/colors';

const drawerWidth = 372;
const minimizedDrawerWidth = 80;

export const Container = styled('div')(() => ({
	display: 'flex',
	backgroundColor: BACKGROUND.BASE[100],
}));

export const Main = styled('main', {
	shouldForwardProp: (prop) => prop !== 'open',
})<{
	open?: boolean;
}>(({ theme, open }) => {
	const { transitions } = theme;
	const { create, duration, easing } = transitions;

	return {
		flexGrow: 1,
		height: '100%',
		overflow: 'auto',
		width: '100%',
		transition: create('margin', {
			easing: easing.sharp,
			duration: duration.leavingScreen,
		}),
		...(open && {
			transition: create('margin', {
				easing: easing.easeOut,
				duration: duration.enteringScreen,
			}),
			marginLeft: 0,
		}),
	};
});

export const AppBar = styled('div')<{ open: boolean }>(({ open }) => ({
	position: 'fixed',
	top: 0,
	right: 0,
	zIndex: 1000,
	width: `calc(100% - ${open ? drawerWidth : minimizedDrawerWidth}px)`,
	marginLeft: `${open ? drawerWidth : minimizedDrawerWidth}px`,
	color: PEARL.BASE[900],
	padding: '16px 32px',
	display: 'flex',
	gap: '16px',
	alignItems: 'center',
	background: `linear-gradient(0deg, ${PEARL.QUATERNARY},${PEARL.QUATERNARY},${BACKGROUND.BASE[72]})`,
	borderBottom: `1px solid ${STROKE.SECONDARY}`,
	backdropFilter: 'blur(20px)',
	transition: `margin 195ms cubic-bezier(0.4, 0, 0.6, 1), width 195ms cubic-bezier(0.4, 0, 0.6, 1)`,
	...(open && {
		transition: 'margin 225ms cubic-bezier(0.0, 0, 0.2, 1), width 225ms cubic-bezier(0.0, 0, 0.2, 1)',
	}),
}));

export const AppBarMobile = styled('div')(() => ({
	position: 'fixed',
	top: 0,
	right: 0,
	zIndex: 1000,
	width: '100%',
	padding: '8px 16px',
	display: 'flex',
	gap: '16px',
	alignItems: 'center',
	background: BACKGROUND.PRIMARY[80],
	borderBottom: `1px solid ${STROKE.SECONDARY}`,
	backdropFilter: 'blur(20px)',
}));

export const DrawerHeader = styled('div')<{ open: boolean }>(({ open }) => ({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	padding: open ? '4px 16px 4px 32px' : '4px 16px 4px 24px',
	width: `${open ? drawerWidth : minimizedDrawerWidth}px`,
	height: '72px',
	overflow: 'hidden',
	position: 'relative',
	borderBottom: `1px solid ${STROKE.SECONDARY}`,
}));

export const TopNavigationPlaceholder = styled('div')<{ open: boolean }>(({ open }) => ({
	display: 'flex',
	alignItems: 'center',
	padding: '4px 16px 4px 32px',
	justifyContent: 'flex-start',
	width: `${open ? drawerWidth : minimizedDrawerWidth}px`,
	height: '72px',
	overflow: 'hidden',
	position: 'relative',
}));

export const DrawerWrapper = styled(Drawer)<DrawerProps>(({ open }) => ({
	width: open ? drawerWidth : minimizedDrawerWidth,
	flexShrink: 0,
	transition: open
		? `margin 225ms cubic-bezier(0.0, 0, 0.2, 1), width 225ms cubic-bezier(0.0, 0, 0.2, 1)`
		: `margin 195ms cubic-bezier(0.4, 0, 0.6, 1), width 195ms cubic-bezier(0.4, 0, 0.6, 1)`,
	'& .MuiDrawer-paper': {
		width: open ? drawerWidth : minimizedDrawerWidth,
		backgroundColor: BACKGROUND.PRIMARY[100],
		border: `1px solid ${STROKE.TERTIARY}`,
		overflow: 'hidden',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		borderRadius: '16px  0px  0px 16px',
		boxShadow: `0px 24px 32px - 8px rgba(0, 0, 0, 0.08), 0px 8px 12px - 8px rgba(0, 0, 0, 0.08)`,
		overflowX: 'hidden!important',
	},
}));

export const DrawerActions = styled('div')(() => ({
	display: 'flex',
	padding: '20px 24px',
	alignItems: 'center',
	gap: '16px',
	alignSelf: 'stretch',
	borderBottom: `1px solid ${STROKE.SECONDARY}`,
	background: BACKGROUND.PRIMARY[100],
}));

export const IMG = styled('img')(() => ({
	height: 'auto',
	width: '100%',
}));

export const Wrapper = styled('div')(() => ({
	backgroundColor: BACKGROUND.BASE[100],
	padding: '24px',
	height: '100%',
	minHeight: 'calc(100vh - 64px)',
	position: 'relative',
	'@media (max-width: 769px)': {
		padding: '24px 0px 90px 0px',
	},
}));

export const CustomToolbar = styled(Toolbar)(() => ({
	justifyContent: 'space-between',
	alignItems: 'center',
}));

export const MenuItem = styled('div')(() => ({
	display: 'flex',
	alignItems: 'center',
	gap: 12,
	padding: '12px 16px',
	userSelect: 'none',
}));

export const MenuItemWrapper = styled('div')<{ isSelected: boolean }>(({ isSelected }) => ({
	...(isSelected && { backgroundColor: PRIMARY.QUATERNARY }),
	borderRadius: '11px',
	':hover': {
		background: PEARL.QUATERNARY,
		boxShadow: '0px 4px 8px -2px rgba(0, 0, 0, 0.12), 0px 2px 4px -2px rgba(0, 0, 0, 0.04)',
	},
}));
